import { useEffect } from 'react';
import { expressionFunction } from 'vega';

/**
 * Split the floor-log10 of abs(value) into divisor and remainder within the 3-decade cycle.
 *   [  0.01 -     0.1[ → exp: -2, remainder: 2, divisor: -3
 *   [   0.1 -       1[ → exp: -1, remainder: 2, divisor: -3
 *   [     1 -      10[ → exp:  0, remainder: 0, divisor:  0
 *   [    10 -     100[ → exp:  1, remainder: 1, divisor:  0
 *   [   100 -    1000[ → exp:  2, remainder: 2, divisor:  0
 *   [  1000 -   10000[ → exp:  3, remainder: 0, divisor:  3
 *   [ 10000 -  100000[ → exp:  4, remainder: 1, divisor:  3
 *   [100000 - 1000000[ → exp:  5, remainder: 2, divisor:  3
 */
export const log10parts = (value: number): [exp: number, remainder: number] => {
  const exp = Math.floor(Math.log10(Math.abs(value)));
  const remainder = ((exp % 3) + 3) % 3;
  const divisor = exp - remainder;
  return [divisor, remainder];
};

const formattedNumberCache = new Map<number, Intl.NumberFormat>();
/**
 * Get a formatted number by locale.
 * @param decimals - number of decimals to format
 * @returns a formatted number by locale
 */
export const getFormattedNumberByLocale = (
  decimals: number
): Intl.NumberFormat => {
  let f = formattedNumberCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formattedNumberCache.set(decimals, f);
  }
  return f;
};

/**
 * Magnitude tiers for dynamic numeric formatting. Must be sorted from largest to smallest.
 */
const TIERS: [number, string][] = [
  //[12, 'tn'],
  [9, 'b'],
  [6, 'm'],
  [3, 'k'],
  [0, ''],
  //[-3, 'milli'],
];

// simple wordwrap function based on an approximation of average character width
export const wordWrap = (x: string, width: number, charAvgWidth = 7.5) => {
  const nbrChar = Math.floor(width / charAvgWidth); // approximation

  return x.split(' ').reduce((acc: string[], cur: string) => {
    if (acc.length === 0) {
      acc.push(cur);
    } else {
      if ((acc.at(-1) ?? '').length + cur.length + 1 >= nbrChar) {
        acc.push(cur);
      } else {
        acc[acc.length - 1] += ' ' + cur;
      }
    }
    return acc;
  }, []);
};

/**
 * Formats a number based on the value's magnitude. A fixed suffix can be set.
 *
 * @param value - numeric value to format
 * @param suffix - suffix to append to the formatted value
 * @param scale - scale factor to apply to the value
 * @param positiveSign - whether to show a positive sign for positive values
 */
export const formatNumeric = (
  value: number,
  suffix = '',
  scale = 1,
  positiveSign = false
): string => {
  if (value === 0) {
    return '0' + suffix;
  }
  const prefix = positiveSign && value > 0 ? '+' : '';
  const [divisor, remainder] = log10parts(scale * value);
  const exp = divisor + remainder;
  const decimals = 1 - exp;
  return (
    prefix +
    getFormattedNumberByLocale(Math.min(Math.max(0, decimals), 3)).format(
      scale * value
    ) +
    suffix
  );
};

/**
 * Formats a number based on the value's magnitude and derive a suitable suffix from TIERS.
 *
 * @param value - numeric value to format
 * @param digits - number of digits before and after the decimal point
 * @param positiveSign - whether to show a positive sign for positive values
 */
export const formatDynamicNumeric = (
  value: number,
  digits = 3,
  positiveSign = false
): string => {
  if (value === 0) {
    return '0';
  }

  const prefix = positiveSign && value > 0 ? '+' : '';
  const sd = Math.max(0, digits - 1);
  // shift value to get exp and remainder for the desired number of digits
  const [divisor, remainder] = log10parts(value * 10 ** (3 - digits));

  for (const [threshold, suffix] of TIERS) {
    if (divisor >= threshold) {
      const scaled = value / 10 ** threshold;
      const absScaled = Math.abs(scaled);
      // digits >=3 → right-shift → absScaled >= 1
      const decimals =
        absScaled < 1
          ? Math.min(sd, 2 - remainder)
          : sd - Math.floor(Math.log10(absScaled));
      return (
        prefix +
        getFormattedNumberByLocale(Math.max(0, decimals)).format(scaled) +
        suffix
      );
    }
  }
  return prefix + getFormattedNumberByLocale(sd).format(value); // smaller than smallest tier
};

/**
 * Formats the axis labels based on the value range and derive a suitable suffix from TIERS.
 *
 * @param value - numeric axis tick value to format
 * @param bounds - min and max value of the axis range
 */
export const formatDynamicNumericAxis = (
  value: number,
  bounds: [number, number]
): string => {
  const maxAbsBound = Math.max(Math.abs(bounds[0]), Math.abs(bounds[1]));
  if (maxAbsBound === 0) {
    return getFormattedNumberByLocale(1).format(value);
  }
  const [divisor, remainder] = log10parts(maxAbsBound);

  for (const [threshold, suffix] of TIERS) {
    if (divisor >= threshold) {
      const scaled = value / 10 ** threshold;
      const decimals = 1 - remainder;
      return (
        getFormattedNumberByLocale(Math.max(0, decimals)).format(scaled) +
        suffix
      );
    }
  }
  return getFormattedNumberByLocale(1).format(value); // smaller than smallest tier
};

/**
 * Formats the axis labels based on the value range. A fixed suffix can be set.
 *
 * @param value - numeric axis tick value to format
 * @param bounds - min and max value of the axis range
 * @param suffix - suffix to append to the formatted value
 * @param scale - scale factor to apply to the value
 * @param zeroBased - flag to indicate if the axis is zero-based
 */
export const formatNumericAxis = (
  value: number,
  bounds: [number, number],
  suffix = '',
  scale = 1,
  zeroBased = true
): string => {
  const range = zeroBased
    ? Math.max(bounds[1], 0) - Math.min(bounds[0], 0)
    : Math.abs(bounds[1] - bounds[0]); // always positive
  if (range === 0) {
    return getFormattedNumberByLocale(0).format(scale * value) + suffix;
  }
  const [divisor, remainder] = log10parts(scale * range);
  const exp = divisor + remainder;
  const scaledRange = (scale * range) / 10 ** exp; // always in interval [1; 10[
  // the number of decimals to display depends on the range of the axis
  // the range is transformed into: range = scaledRange * 10^exp
  // the decimals are inversely proportional to the exponent
  const decimals = (scaledRange <= 4 ? 1 : 0) - exp;
  return (
    getFormattedNumberByLocale(Math.min(Math.max(0, decimals), 3)).format(
      scale * value
    ) + suffix
  );
};

export const useVegaFunctions = () => {
  useEffect(() => {
    expressionFunction('amWordWrap', wordWrap);
    expressionFunction('amFormatDynamicNumeric', formatDynamicNumeric);
    expressionFunction('amFormatDynamicNumericAxis', formatDynamicNumericAxis);
    expressionFunction('amFormatNumeric', formatNumeric);
    expressionFunction('amFormatNumericAxis', formatNumericAxis);
  }, []);
};
